// Page marks for the source viewer: the focus kinds (requirement, clause, cited texts), queries for page highlights or located texts normalised into one ViewerMarks shape (a line shared by overlapping citations is marked once), paragraph blocks from clause lines, scroll targets and figure tags; rectangles stay in PDF points (page.rect space) and are scaled by the layer.
import { useQuery } from "@tanstack/react-query";

import type { FigureMark, LocateResult, PageHighlights, Rect, ValueMark } from "@/api/__generated__";
import { sourcesLocateTexts } from "@/api/__generated__";
import { sourcesGetPageHighlightsOptions } from "@/api/__generated__/@tanstack/react-query.gen";

export type PdfFocus =
  | { kind: "requirement"; reqUid: string }
  | { kind: "clause"; clauseUid: string }
  | { kind: "texts"; texts: { key: string; text: string }[] }
  | null;

export type ScrollTarget = "clause" | "requirement" | "figure" | "top";

export interface ViewerMarks {
  pageWidth: number;
  pageHeight: number;
  clause: Rect[];
  text: Rect[];
  textFound: boolean | null;
  values: ValueMark[];
  figures: FigureMark[];
}

export interface PageMarksState {
  marks: ViewerMarks | null;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
}

const HOUR_MS = 3_600_000;
const BLOCK_GAP_LINES = 1.2;

export function marksFromHighlights(highlights: PageHighlights): ViewerMarks {
  return {
    pageWidth: highlights.page_width,
    pageHeight: highlights.page_height,
    clause: highlights.clause?.rects ?? [],
    text: highlights.requirement?.rects ?? [],
    textFound: highlights.requirement === null ? null : highlights.requirement.found,
    values: highlights.values,
    figures: highlights.figures,
  };
}

export function rectKey(rect: Rect): string {
  return `${String(rect.x0)}:${String(rect.y0)}:${String(rect.x1)}:${String(rect.y1)}`;
}

function uniqueRects(rects: readonly Rect[]): Rect[] {
  const seen = new Set<string>();
  return rects.filter((rect) => {
    const key = rectKey(rect);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export function marksFromLocate(result: LocateResult): ViewerMarks {
  return {
    pageWidth: result.page_width,
    pageHeight: result.page_height,
    clause: [],
    text: uniqueRects(result.matches.flatMap((match) => match.rects)),
    textFound: result.matches.some((match) => match.found),
    values: [],
    figures: [],
  };
}

export function focusKey(focus: PdfFocus): string | null {
  if (focus === null) {
    return null;
  }
  if (focus.kind === "requirement") {
    return `requirement:${focus.reqUid}`;
  }
  if (focus.kind === "clause") {
    return `clause:${focus.clauseUid}`;
  }
  return `texts:${focus.texts.map((item) => `${item.key}=${item.text}`).join("|")}`;
}

function highlightQuery(focus: PdfFocus): { req_uid?: string; clause_uid?: string } {
  if (focus?.kind === "requirement") {
    return { req_uid: focus.reqUid };
  }
  if (focus?.kind === "clause") {
    return { clause_uid: focus.clauseUid };
  }
  return {};
}

export function usePageMarks(docUid: string, page: number, focus: PdfFocus): PageMarksState {
  const byHighlights = focus?.kind === "requirement" || focus?.kind === "clause";
  const texts = focus?.kind === "texts" ? focus.texts : null;
  const highlights = useQuery({
    ...sourcesGetPageHighlightsOptions({ path: { doc_uid: docUid, page }, query: highlightQuery(focus) }),
    enabled: byHighlights,
    staleTime: HOUR_MS,
    select: marksFromHighlights,
  });
  const located = useQuery({
    queryKey: ["kg", "sourcesLocateTexts", docUid, page, texts],
    queryFn: async ({ signal }) => {
      const { data } = await sourcesLocateTexts({
        path: { doc_uid: docUid, page },
        body: { texts: texts ?? [] },
        signal,
        throwOnError: true,
      });
      return data;
    },
    enabled: texts !== null && texts.length > 0,
    staleTime: HOUR_MS,
    select: marksFromLocate,
  });
  const active = byHighlights ? highlights : located;
  const enabled = byHighlights || (texts !== null && texts.length > 0);
  return {
    marks: enabled ? (active.data ?? null) : null,
    isLoading: enabled && active.isLoading,
    error: enabled ? active.error : null,
    refetch: () => {
      void active.refetch();
    },
  };
}

export function paragraphBlocks(rects: readonly Rect[]): Rect[] {
  const blocks: Rect[] = [];
  for (const rect of [...rects].sort((left, right) => left.y0 - right.y0)) {
    const last = blocks.at(-1);
    const lineHeight = rect.y1 - rect.y0;
    if (last !== undefined && rect.y0 - last.y1 <= lineHeight * BLOCK_GAP_LINES) {
      blocks[blocks.length - 1] = {
        x0: Math.min(last.x0, rect.x0),
        y0: last.y0,
        x1: Math.max(last.x1, rect.x1),
        y1: Math.max(last.y1, rect.y1),
      };
    } else {
      blocks.push({ ...rect });
    }
  }
  return blocks;
}

export function scrollAnchor(marks: ViewerMarks | null, target: ScrollTarget): number | null {
  if (marks === null || target === "top") {
    return target === "top" ? 0 : null;
  }
  const first = (rects: readonly Rect[]) =>
    rects.length > 0 ? Math.min(...rects.map((rect) => rect.y0)) : null;
  if (target === "figure") {
    const figure = marks.figures.find((item) => item.curve_count > 0) ?? marks.figures[0];
    return figure?.bbox[1] ?? null;
  }
  if (target === "clause") {
    return first(marks.clause) ?? first(marks.text);
  }
  return first(marks.text) ?? first(marks.clause);
}

export function figureTag(figure: FigureMark): string {
  const points = `${String(figure.point_count)} ${figure.point_count === 1 ? "point" : "points"}`;
  return figure.curve_count === 1
    ? `Extracted as curve · ${points}`
    : `Extracted as ${String(figure.curve_count)} curves · ${points}`;
}
